/**
 * Per-feature OpenSeadragon support. Code defaults below; organisations override the mode of any
 * feature in `OrganisationSetting.config.osd_viewer.feature_registry`.
 *
 * Modes:
 *   native          works under OSD
 *   fallback_prompt superusers confirm a switch to OpenLayers; others switch automatically
 *   fallback_auto   switch to OpenLayers without asking
 *   hidden          hidden under OSD (superusers can still switch to OpenLayers manually)
 */
import {aclPermissions} from "../../../const/ACLPermissions";

export const FEATURE_MODES = {
    NATIVE: 'native',
    FALLBACK_PROMPT: 'fallback_prompt',
    FALLBACK_AUTO: 'fallback_auto',
    HIDDEN: 'hidden',
};

export const OSD_FEATURES = {
    annotations: {
        label: 'Annotations',
        mode: FEATURE_MODES.NATIVE,
        appIds: [aclPermissions.view_own_annotation],
    },
    edit_anno_label: {
        label: 'Edit annotation label',
        mode: FEATURE_MODES.FALLBACK_PROMPT,
        appIds: [aclPermissions.edit_anno_label_app],
    },
    magic_tool: {label: 'Magic Tool', mode: FEATURE_MODES.NATIVE, appIds: []},
    line_measure: {label: 'Line measurement', mode: FEATURE_MODES.FALLBACK_PROMPT, appIds: []},
    tile_view: {
        label: 'Tile View',
        mode: FEATURE_MODES.FALLBACK_PROMPT,
        appIds: [aclPermissions.tile_view_app],
    },
    grid_overlay: {
        label: 'Grid overlay',
        mode: FEATURE_MODES.FALLBACK_PROMPT,
        appIds: [aclPermissions.grid_overlay_app],
    },
    indx: {label: 'Indx', mode: FEATURE_MODES.FALLBACK_PROMPT, appIds: [aclPermissions.indx_app]},
    ihc: {label: 'IHC', mode: FEATURE_MODES.FALLBACK_PROMPT, appIds: [aclPermissions.ihc_app]},
    deep_bio: {label: 'DeepBio', mode: FEATURE_MODES.FALLBACK_PROMPT, appIds: [aclPermissions.deep_bio_app]},
    background_blend: {
        label: 'Background Blend',
        mode: FEATURE_MODES.FALLBACK_PROMPT,
        appIds: [aclPermissions.background_blend_app],
    },
    ai_chat: {label: 'Morpho Chat', mode: FEATURE_MODES.FALLBACK_PROMPT, appIds: [aclPermissions.ai_chat_app]},
    // Always-visible controls have no "open" action to intercept, so they are hidden rather than prompted.
    layers_control: {label: 'Layers Control', mode: FEATURE_MODES.HIDDEN, appIds: [aclPermissions.layers_control_app]},
    preview: {label: 'Preview', mode: FEATURE_MODES.HIDDEN, appIds: [aclPermissions.preview_controls_app]},
    z_stack: {label: 'Z-stack', mode: FEATURE_MODES.FALLBACK_AUTO, appIds: []},
    exposure_stack: {label: 'Exposure stack', mode: FEATURE_MODES.FALLBACK_AUTO, appIds: []},
    blood_mode: {label: 'Blood mode', mode: FEATURE_MODES.FALLBACK_AUTO, appIds: []},
    // splitSyncUtils.syncViews listens to OL view events and steps whole zoom levels.
    split_sync: {label: 'Split-view sync', mode: FEATURE_MODES.FALLBACK_AUTO, appIds: []},
};

const MODES = new Set(Object.values(FEATURE_MODES));

/** `osdViewerConfig` is `state.osdViewerConfigReducer`. Unknown features are treated as native. */
export const getFeatureMode = (featureKey, osdViewerConfig) => {
    const override = osdViewerConfig?.organisation?.feature_registry?.[featureKey];
    if (MODES.has(override)) return override;
    return OSD_FEATURES[featureKey]?.mode ?? FEATURE_MODES.NATIVE;
};

export const isFallbackMode = (mode) =>
    mode === FEATURE_MODES.FALLBACK_PROMPT || mode === FEATURE_MODES.FALLBACK_AUTO;

export const getFeatureForApp = (app) => {
    if (!app?.id) return null;
    return Object.keys(OSD_FEATURES).find(key => OSD_FEATURES[key].appIds.includes(app.id)) ?? null;
};

export const getFeatureLabel = (featureKey) => OSD_FEATURES[featureKey]?.label ?? featureKey;
